import React, { useState, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import {
  Button,
  Table,
  Space,
  Empty,
  Tag,
  Input,
  Select,
  Switch,
  Modal,
  Pagination,
  Card,
  Row,
  Col,
  DatePicker,
  InputNumber,
  message
} from 'antd'
import {
  ReloadOutlined,
  ArrowLeftOutlined,
  WarningOutlined,
  SendOutlined,
  RedoOutlined
} from '@ant-design/icons'
import PageLoader from '../../../components/Layouts/PageLoader'
import {
  getRequest,
  postRequest,
  dutyBroadcastDetailUrl,
  dutyBroadcastCandidatesUrl,
  dutyBroadcastAddRecipientsUrl,
  dutyBroadcastAddSegmentUrl,
  dutyBroadcastRemoveRecipientsUrl,
  dutyBroadcastRecipientsUrl,
  dutyBroadcastStatsUrl,
  dutyBroadcastScheduleUrl,
  dutyBroadcastSendNowUrl,
  dutyBroadcastPauseUrl,
  dutyBroadcastResumeUrl
} from '../../../config'
import { formatMoney } from '../../Analytics/utils'
import CustomerDetailBody from '../Customers/CustomerDetailBody'
import { broadcastStatusTag, recipientStatusTag } from './statusColors'
import IdentityLabel from '../../../components/IdentityLabel'
import retryRecipients from './retryRecipients'

const EMPTY_FILTERS = {
  search: '',
  withinWindow: false,
  activeWithin: '',
  activeWithinUnit: 'days',
  idleMoreThanDays: '',
  hasPaid: '',
  isDealer: false,
  minRevenue: '',
  minRequests: '',
  ordering: '-last_activity'
}

// Build the filter body posted to /candidates/ and /add-segment/, omitting blanks so the two
// always agree on "who matches" (segment add == what's on screen).
const buildFilterBody = f => {
  const body = {}
  if (f.search) body.search = f.search
  if (f.withinWindow) body.withinWindow = true
  if (f.activeWithin !== '' && f.activeWithin != null) {
    body.activeWithin = f.activeWithin
    body.activeWithinUnit = f.activeWithinUnit
  }
  if (f.idleMoreThanDays !== '' && f.idleMoreThanDays != null)
    body.idleMoreThanDays = f.idleMoreThanDays
  if (f.hasPaid !== '') body.hasPaid = f.hasPaid
  if (f.isDealer) body.isDealer = true
  if (f.minRevenue !== '' && f.minRevenue != null) body.minRevenue = f.minRevenue
  if (f.minRequests !== '' && f.minRequests != null) body.minRequests = f.minRequests
  if (f.ordering) body.ordering = f.ordering
  return body
}

const membershipBadge = r => {
  if (r.alreadySent)
    return (
      <Tag color="volcano" style={{ marginBottom: 0 }}>
        Already sent
      </Tag>
    )
  if (r.inBroadcast)
    return (
      <Tag color="blue" style={{ marginBottom: 0 }}>
        In list
      </Tag>
    )
  return null
}

const RecipientSelect = props => {
  const broadcastId = props.match.params.id
  const [loading, setLoading] = useState(false)
  const [broadcast, setBroadcast] = useState(null)

  // Candidate table.
  const [candidates, setCandidates] = useState([])
  const [candMeta, setCandMeta] = useState({ count: 0, page: 1, pageSize: 50 })
  const [candPage, setCandPage] = useState(1)
  const [filters, setFilters] = useState(EMPTY_FILTERS)
  const [selectedKeys, setSelectedKeys] = useState([])
  const [quickViewId, setQuickViewId] = useState(null)
  const [reAddTarget, setReAddTarget] = useState(null)
  const [manualNumbers, setManualNumbers] = useState('')

  // Pending panel. The recipient list is paginated server-side (same as the candidate table) so
  // a list longer than one page is fully reachable.
  const [recipients, setRecipients] = useState([])
  const [recMeta, setRecMeta] = useState({ count: 0, page: 1, pageSize: 50 })
  const [recPage, setRecPage] = useState(1)
  const [recLoading, setRecLoading] = useState(false)
  // Whole-list counts. These MUST come from the server: a single page of rows can't tell us how
  // many recipients are pending/failed/outside the 24h window overall.
  const [stats, setStats] = useState(null)
  const [scheduleAt, setScheduleAt] = useState(null)
  const [busy, setBusy] = useState(false)
  const [retrying, setRetrying] = useState(false)

  const loadBroadcast = useCallback(async () => {
    const res = await getRequest(dutyBroadcastDetailUrl(broadcastId), 'broadcast', setLoading)
    if (res.success) setBroadcast(res.data)
  }, [broadcastId])

  const loadCandidates = useCallback(
    async (page = candPage, f = filters) => {
      const url = `${dutyBroadcastCandidatesUrl(broadcastId)}?page=${page}`
      const res = await postRequest(url, buildFilterBody(f), 'candidates', setLoading)
      if (res.success) {
        setCandidates(res.data.results || res.data)
        setCandMeta({
          count: res.data.count || 0,
          page: res.data.page || page,
          pageSize: res.data.pageSize || 50
        })
      }
    },
    [broadcastId, candPage, filters]
  )

  const loadRecipients = useCallback(
    async (page = 1) => {
      const res = await getRequest(
        `${dutyBroadcastRecipientsUrl(broadcastId)}?page=${page}`,
        'recipients',
        setRecLoading
      )
      if (!res.success) return
      const rows = res.data.results || res.data
      const count = res.data.count == null ? rows.length : res.data.count
      const pageSize = res.data.pageSize || 50
      // Removing the last row on the final page leaves that page empty — step back rather than
      // showing an empty table with no way out.
      if (page > 1 && rows.length === 0 && count > 0) {
        const lastPage = Math.max(1, Math.ceil(count / pageSize))
        setRecPage(lastPage)
        return loadRecipients(lastPage)
      }
      setRecipients(rows)
      setRecMeta({ count, page: res.data.page || page, pageSize })
      setRecPage(res.data.page || page)
    },
    [broadcastId]
  )

  const loadStats = useCallback(async () => {
    const res = await getRequest(dutyBroadcastStatsUrl(broadcastId), 'stats', () => {})
    if (res.success) setStats(res.data)
  }, [broadcastId])

  useEffect(() => {
    loadBroadcast()
    loadCandidates(1, EMPTY_FILTERS)
    loadRecipients(1)
    loadStats()
    // eslint-disable-next-line
  }, [broadcastId])

  const applyFilters = () => {
    setCandPage(1)
    loadCandidates(1, filters)
  }
  const clearFilters = () => {
    setFilters(EMPTY_FILTERS)
    setCandPage(1)
    loadCandidates(1, EMPTY_FILTERS)
  }
  const setF = (key, value) => setFilters(prev => ({ ...prev, [key]: value }))

  const refreshAll = () => {
    loadBroadcast()
    loadCandidates(candPage, filters)
    loadRecipients(recPage)
    loadStats()
  }

  const summaryToast = s => {
    const parts = []
    if (s.added) parts.push(`${s.added} added`)
    if (s.resent) parts.push(`${s.resent} re-added`)
    if (s.skippedExisting) parts.push(`${s.skippedExisting} already in list`)
    if (s.invalid) parts.push(`${s.invalid} invalid`)
    message.success(parts.length ? parts.join(', ') : 'Nothing to add')
    if (s.invalidPhones && s.invalidPhones.length)
      message.warning(`Invalid numbers: ${s.invalidPhones.join(', ')}`)
  }

  const addSelected = async () => {
    if (!selectedKeys.length) {
      message.warning('Tick at least one customer.')
      return
    }
    const res = await postRequest(
      dutyBroadcastAddRecipientsUrl(broadcastId),
      { customerIds: selectedKeys },
      'recipients',
      setBusy
    )
    if (res.success) {
      summaryToast(res.data)
      setSelectedKeys([])
      refreshAll()
    }
  }

  const addSegment = async () => {
    Modal.confirm({
      title: 'Add everyone matching these filters?',
      content:
        'This adds every customer matching the current filters to the pending list (already-sent customers are skipped).',
      okText: 'Add all matching',
      onOk: async () => {
        const res = await postRequest(
          dutyBroadcastAddSegmentUrl(broadcastId),
          buildFilterBody(filters),
          'recipients',
          setBusy
        )
        if (res.success) {
          summaryToast(res.data)
          if (res.data.capped)
            message.warning(`Only the first ${res.data.cap} were added (segment cap).`)
          refreshAll()
        }
      }
    })
  }

  const addManual = async () => {
    const phones = manualNumbers
      .split(/[\s,;\n]+/)
      .map(s => s.trim())
      .filter(Boolean)
    if (!phones.length) {
      message.warning('Type at least one phone number.')
      return
    }
    const res = await postRequest(
      dutyBroadcastAddRecipientsUrl(broadcastId),
      { phones },
      'recipients',
      setBusy
    )
    if (res.success) {
      summaryToast(res.data)
      setManualNumbers('')
      refreshAll()
    }
  }

  const confirmReAdd = async () => {
    const target = reAddTarget
    setReAddTarget(null)
    const res = await postRequest(
      dutyBroadcastAddRecipientsUrl(broadcastId),
      { customerIds: [target.id], resend: true },
      'recipients',
      setBusy
    )
    if (res.success) {
      summaryToast(res.data)
      refreshAll()
    }
  }

  // Failed recipients can be requeued from here too — the same action the detail page offers,
  // so a send that failed is fixable wherever the recipient is visible.
  const byStatus = (stats && stats.byStatus) || {}
  const failedCount = byStatus.failed || 0

  const doRetry = recipientIds =>
    retryRecipients({
      broadcastId,
      recipientIds,
      failedCount,
      setLoading: setRetrying,
      onDone: refreshAll
    })

  const removeRecipient = async recipientId => {
    const res = await postRequest(
      dutyBroadcastRemoveRecipientsUrl(broadcastId),
      { recipientIds: [recipientId] },
      'recipients',
      setBusy
    )
    if (res.success) {
      message.success('Removed')
      refreshAll()
    }
  }

  // -- schedule / send -----------------------------------------------------
  const outsideWindowCount = (stats && stats.outsideWindowPending) || 0
  const pendingCount = byStatus.pending || 0

  const doSchedule = async () => {
    if (!scheduleAt) {
      message.warning('Pick a date & time.')
      return
    }
    const res = await postRequest(
      dutyBroadcastScheduleUrl(broadcastId),
      { scheduledAt: scheduleAt.toISOString() },
      'schedule',
      setBusy
    )
    if (res.success) {
      message.success('Scheduled')
      loadBroadcast()
    } else if (res.error && res.error.response) {
      message.error(res.error.response.data.detail || 'Could not schedule.')
    }
  }

  const doSendNow = () => {
    Modal.confirm({
      title: 'Send this broadcast now?',
      icon: <SendOutlined />,
      content: (
        <div>
          <p>
            {pendingCount} recipient{pendingCount === 1 ? '' : 's'} will be messaged.
          </p>
          {outsideWindowCount ? (
            <p style={{ color: '#d46b08' }}>
              {outsideWindowCount} are outside the 24h window and will only receive the
              message if an approved marketing template is configured (otherwise skipped).
            </p>
          ) : null}
        </div>
      ),
      okText: 'Send now',
      onOk: async () => {
        const res = await postRequest(
          dutyBroadcastSendNowUrl(broadcastId),
          {},
          'send',
          setBusy
        )
        if (res.success) {
          message.success('Sending started')
          refreshAll()
        } else if (res.error && res.error.response) {
          message.error(res.error.response.data.detail || 'Could not send.')
        }
      }
    })
  }

  const doPauseResume = async () => {
    const url =
      broadcast.status === 'paused'
        ? dutyBroadcastResumeUrl(broadcastId)
        : dutyBroadcastPauseUrl(broadcastId)
    const res = await postRequest(url, {}, 'broadcast', setBusy)
    if (res.success) {
      message.success(broadcast.status === 'paused' ? 'Resumed' : 'Paused')
      loadBroadcast()
    }
  }

  // Recipients/message are editable in any status (operators may revise and re-use a sent
  // broadcast); only an in-progress send is off-limits to avoid mutating a live dispatch.
  const canEdit = broadcast && broadcast.status !== 'sending'
  // Scheduling (a future go-live) only makes sense before the first send.
  const canSchedule = broadcast && ['draft', 'scheduled'].includes(broadcast.status)

  const candColumns = [
    {
      title: 'Customer',
      key: 'customer',
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          <Space size={6}>
            <IdentityLabel value={r} strong />
            {membershipBadge(r)}
          </Space>
          {r.momoName ? (
            <span style={{ color: '#888', fontSize: 12 }}>{r.momoName}</span>
          ) : null}
        </Space>
      )
    },
    { title: 'Requests', dataIndex: 'requestCount', key: 'requestCount' },
    { title: 'Paid', dataIndex: 'paidCount', key: 'paidCount' },
    {
      title: 'Revenue',
      dataIndex: 'revenue',
      key: 'revenue',
      render: v => formatMoney(v, 'GHS')
    },
    {
      title: 'Last active',
      key: 'idle',
      render: (_, r) => (
        <Space size={4}>
          <span>{r.idleDisplay}</span>
          {r.withinWindow ? <Tag color="green">24h</Tag> : null}
        </Space>
      )
    },
    {
      title: 'Action',
      key: 'action',
      width: 170,
      render: (_, r) => (
        <Space size={4}>
          <Button size="small" onClick={() => setQuickViewId(r.id)}>
            Quick view
          </Button>
          {r.alreadySent && canEdit ? (
            <Button size="small" danger onClick={() => setReAddTarget(r)}>
              Re-add
            </Button>
          ) : null}
        </Space>
      )
    }
  ]

  const recColumns = [
    {
      title: 'Recipient',
      key: 'recipient',
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          <Space size={6}>
            <IdentityLabel value={r} strong />
            {r.alreadySent ? (
              <Tag color="volcano" style={{ marginBottom: 0 }}>
                Already sent
              </Tag>
            ) : null}
          </Space>
          {r.displayName ? (
            <span style={{ color: '#888', fontSize: 12 }}>{r.displayName}</span>
          ) : null}
        </Space>
      )
    },
    { title: 'Status', key: 'status', render: (_, r) => recipientStatusTag(r.status) },
    {
      title: 'Last active',
      key: 'idle',
      render: (_, r) => (
        <Space size={4}>
          <span>{r.idleDisplay}</span>
          {r.withinWindow ? <Tag color="green">24h</Tag> : null}
        </Space>
      )
    },
    {
      title: '',
      key: 'action',
      width: 140,
      render: (_, r) => (
        <Space size={4}>
          {r.status === 'failed' ? (
            <Button size="small" icon={<RedoOutlined />} onClick={() => doRetry([r.id])}>
              Retry
            </Button>
          ) : null}
          {r.status === 'pending' && canEdit ? (
            <Button size="small" type="link" danger onClick={() => removeRecipient(r.id)}>
              Remove
            </Button>
          ) : null}
        </Space>
      )
    }
  ]

  return (
    <PageLoader className="container" loading={loading}>
      <div className="card mb-5rem">
        <div className="card-body">
          <Space style={{ marginBottom: 8 }} wrap>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => props.history.push('/settings/broadcasts')}
            >
              Back
            </Button>
            <Button icon={<ReloadOutlined />} onClick={refreshAll}>
              Refresh
            </Button>
            {failedCount > 0 ? (
              <Button
                danger
                icon={<RedoOutlined />}
                loading={retrying}
                onClick={() => doRetry(null)}
              >
                Retry failed ({failedCount})
              </Button>
            ) : null}
          </Space>
          <h3 style={{ marginBottom: 4 }}>
            {broadcast ? broadcast.name : 'Broadcast'}{' '}
            {broadcast ? broadcastStatusTag(broadcast.status) : null}
          </h3>
          {broadcast && broadcast.body ? (
            <p style={{ color: '#888', marginBottom: 4, whiteSpace: 'pre-wrap' }}>
              {broadcast.body}
            </p>
          ) : null}
          {canEdit ? (
            <Button
              type="link"
              style={{ padding: 0 }}
              onClick={() =>
                props.history.push(`/settings/broadcasts/${broadcastId}/compose`)
              }
            >
              Edit message
            </Button>
          ) : null}

          <Row gutter={16} style={{ marginTop: 12 }}>
            {/* Selection table */}
            <Col xs={24} lg={15}>
              <Card
                size="small"
                title={`Choose customers (${candMeta.count})`}
                bodyStyle={{ padding: 12 }}
              >
                <Space wrap style={{ marginBottom: 12 }}>
                  <Input.Search
                    allowClear
                    placeholder="Phone, @username or MoMo name"
                    style={{ width: 200 }}
                    value={filters.search}
                    onChange={e => setF('search', e.target.value)}
                    onSearch={applyFilters}
                  />
                  <Space size={4}>
                    <Switch
                      checked={filters.withinWindow}
                      onChange={v => setF('withinWindow', v)}
                    />
                    <span>Within 24h</span>
                  </Space>
                  <Space size={4}>
                    <span>Active within</span>
                    <InputNumber
                      min={1}
                      style={{ width: 70 }}
                      value={filters.activeWithin || undefined}
                      onChange={v => setF('activeWithin', v == null ? '' : v)}
                    />
                    <Select
                      style={{ width: 90 }}
                      value={filters.activeWithinUnit}
                      onChange={v => setF('activeWithinUnit', v)}
                      options={[
                        { value: 'hours', label: 'hours' },
                        { value: 'days', label: 'days' }
                      ]}
                    />
                  </Space>
                  <Space size={4}>
                    <span>Idle over</span>
                    <InputNumber
                      min={1}
                      style={{ width: 70 }}
                      value={filters.idleMoreThanDays || undefined}
                      onChange={v => setF('idleMoreThanDays', v == null ? '' : v)}
                    />
                    <span>days</span>
                  </Space>
                  <Select
                    style={{ width: 130 }}
                    value={filters.hasPaid}
                    onChange={v => setF('hasPaid', v)}
                    options={[
                      { value: '', label: 'Any payment' },
                      { value: 'true', label: 'Has paid' },
                      { value: 'false', label: 'Never paid' }
                    ]}
                  />
                  <Space size={4}>
                    <Switch
                      checked={filters.isDealer}
                      onChange={v => setF('isDealer', v)}
                    />
                    <span>Dealers</span>
                  </Space>
                  <InputNumber
                    min={0}
                    placeholder="Min revenue"
                    style={{ width: 110 }}
                    value={filters.minRevenue || undefined}
                    onChange={v => setF('minRevenue', v == null ? '' : v)}
                  />
                  <InputNumber
                    min={0}
                    placeholder="Min requests"
                    style={{ width: 110 }}
                    value={filters.minRequests || undefined}
                    onChange={v => setF('minRequests', v == null ? '' : v)}
                  />
                  <Select
                    style={{ width: 170 }}
                    value={filters.ordering}
                    onChange={v => setF('ordering', v)}
                    options={[
                      { value: '-last_activity', label: 'Most recently active' },
                      { value: 'last_activity', label: 'Longest idle' },
                      { value: '-request_count', label: 'Most requests' },
                      { value: '-paid_count', label: 'Most paid' },
                      { value: '-revenue', label: 'Highest revenue' }
                    ]}
                  />
                  <Button type="primary" onClick={applyFilters}>
                    Apply
                  </Button>
                  <Button onClick={clearFilters}>Clear</Button>
                </Space>

                <Table
                  rowKey="id"
                  size="small"
                  dataSource={candidates}
                  columns={candColumns}
                  scroll={{ x: 720 }}
                  pagination={false}
                  rowSelection={
                    canEdit
                      ? {
                          selectedRowKeys: selectedKeys,
                          onChange: setSelectedKeys,
                          getCheckboxProps: r => ({ disabled: !!r.alreadySent })
                        }
                      : undefined
                  }
                  locale={{ emptyText: <Empty description="No matching customers" /> }}
                />
                <div style={{ marginTop: 12, textAlign: 'right' }}>
                  <Pagination
                    current={candMeta.page}
                    total={candMeta.count}
                    pageSize={candMeta.pageSize}
                    showSizeChanger={false}
                    onChange={page => {
                      setCandPage(page)
                      loadCandidates(page, filters)
                    }}
                  />
                </div>

                {canEdit ? (
                  <Space wrap style={{ marginTop: 12 }}>
                    <Button
                      type="primary"
                      loading={busy}
                      disabled={!selectedKeys.length}
                      onClick={addSelected}
                    >
                      Add selected ({selectedKeys.length})
                    </Button>
                    <Button loading={busy} onClick={addSegment}>
                      Add all matching
                    </Button>
                  </Space>
                ) : null}

                {canEdit ? (
                  <div style={{ marginTop: 12 }}>
                    <label style={{ fontWeight: 600 }}>Add phone numbers manually</label>
                    <Input.TextArea
                      rows={2}
                      placeholder="0244000000, 0201234567 …"
                      value={manualNumbers}
                      onChange={e => setManualNumbers(e.target.value)}
                    />
                    <Button
                      style={{ marginTop: 8 }}
                      loading={busy}
                      onClick={addManual}
                    >
                      Add numbers
                    </Button>
                  </div>
                ) : null}
              </Card>
            </Col>

            {/* Pending panel */}
            <Col xs={24} lg={9}>
              <Card
                size="small"
                title={`Recipient list (${recMeta.count})`}
                bodyStyle={{ padding: 12 }}
                extra={
                  <Button
                    size="small"
                    icon={<ReloadOutlined />}
                    loading={recLoading}
                    onClick={() => {
                      loadRecipients(recPage)
                      loadStats()
                    }}
                  />
                }
              >
                <Table
                  rowKey="id"
                  size="small"
                  loading={recLoading}
                  dataSource={recipients}
                  columns={recColumns}
                  scroll={{ x: 420 }}
                  pagination={false}
                  locale={{ emptyText: <Empty description="No recipients yet" /> }}
                />
                {recMeta.count > recMeta.pageSize ? (
                  <div style={{ textAlign: 'right', marginTop: 8 }}>
                    <Pagination
                      size="small"
                      current={recMeta.page}
                      total={recMeta.count}
                      pageSize={recMeta.pageSize}
                      showSizeChanger={false}
                      onChange={page => loadRecipients(page)}
                    />
                  </div>
                ) : null}

                <div style={{ marginTop: 12 }}>
                  <label style={{ fontWeight: 600 }}>Schedule</label>
                  <Space style={{ display: 'flex', marginBottom: 12 }}>
                    <DatePicker
                      showTime
                      style={{ flex: 1 }}
                      value={scheduleAt}
                      onChange={setScheduleAt}
                      disabled={!canSchedule}
                    />
                    <Button loading={busy} disabled={!canSchedule} onClick={doSchedule}>
                      Schedule
                    </Button>
                  </Space>

                  <Space wrap>
                    <Button
                      type="primary"
                      icon={<SendOutlined />}
                      loading={busy}
                      disabled={
                        !broadcast ||
                        broadcast.status === 'sending' ||
                        !pendingCount
                      }
                      onClick={doSendNow}
                    >
                      Send now
                    </Button>
                    {broadcast &&
                    ['scheduled', 'sending', 'paused'].includes(broadcast.status) ? (
                      <Button loading={busy} onClick={doPauseResume}>
                        {broadcast.status === 'paused' ? 'Resume' : 'Pause'}
                      </Button>
                    ) : null}
                  </Space>
                </div>
              </Card>
            </Col>
          </Row>
        </div>
      </div>

      {/* Quick-view modal — reuses the customer detail body. */}
      <Modal
        open={!!quickViewId}
        onCancel={() => setQuickViewId(null)}
        footer={null}
        width={880}
        title="Customer detail"
        destroyOnClose
      >
        {quickViewId ? <CustomerDetailBody id={quickViewId} /> : null}
      </Modal>

      {/* Re-add warning modal. */}
      <Modal
        open={!!reAddTarget}
        onCancel={() => setReAddTarget(null)}
        onOk={confirmReAdd}
        okText="Re-add for re-send"
        okButtonProps={{ danger: true }}
        title={
          <Space>
            <WarningOutlined style={{ color: '#faad14' }} />
            Re-add an already-sent customer?
          </Space>
        }
      >
        <p>
          {reAddTarget ? reAddTarget.displayIdentity || reAddTarget.phoneMasked : ''} has already received this broadcast.
          Re-adding them stages a deliberate re-send. They will still be marked
          &quot;already sent&quot; on both lists.
        </p>
      </Modal>
    </PageLoader>
  )
}

RecipientSelect.propTypes = {
  history: PropTypes.object,
  match: PropTypes.object
}

export default RecipientSelect
